import type { UserSettings } from '@atnt/shared';
import { meQuery } from '@/shared/api/auth';
import { queryClient } from '@/shared/api/query-client';
import { applyInterfacePreferences, resolveTheme } from './preferences';

/**
 * Keeps the document in sync with the signed-in user's saved "Interfeys sozlamalari" outside the settings page.
 *
 * The `_authed` route guard applies only theme / accent / language (shared `applyUserPreferences`) and writes the raw
 * theme, so "Tizim" would end up as `data-theme="system"` (= dark) and the text size, animations and compact tables
 * would never be applied after a reload. This module (imported for its side effects by the settings routes, which the
 * route tree evaluates at start-up):
 *   - applies the full interface settings when the `me` query first resolves and whenever the *saved* values change
 *     (login, "Saqlash", another user signing in) — not on every refetch, so the live preview of the settings page
 *     is never overwritten by an unchanged server copy;
 *   - resolves `data-theme="system"` to light/dark whenever anything writes it (the guard runs on every navigation).
 */

type Saved = Partial<UserSettings> | undefined;

const KEYS = ['locale', 'theme', 'accentColor', 'fontSize', 'compactTables', 'animations'] as const;
let lastApplied = '';

function savedSettings(): Saved {
  return queryClient.getQueryData(meQuery.queryKey)?.user.settings;
}

function sync() {
  const settings = savedSettings();
  if (!settings) {
    lastApplied = '';
    return;
  }
  const key = JSON.stringify(KEYS.map((k) => settings[k] ?? null));
  if (key === lastApplied) return;
  lastApplied = key;
  applyInterfacePreferences(settings);
}

function resolveSystemTheme() {
  const root = document.documentElement;
  if (root.dataset.theme === 'system') root.dataset.theme = resolveTheme('system');
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  queryClient.getQueryCache().subscribe((event) => {
    const [scope, name] = event.query.queryKey as readonly unknown[];
    if (scope === 'auth' && name === 'me' && (event.type === 'updated' || event.type === 'added' || event.type === 'removed')) sync();
  });
  new MutationObserver(resolveSystemTheme).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  resolveSystemTheme();
  sync();
}

